import { useMemo, useEffect, useState, useCallback, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { DocTypeListPageShell } from "@/features/common/components/DocTypeListPageShell";
import { LIST_PAGE_SHELL } from "@/components/layout/pageShell";
import { useItemPriceListWithPagination } from "./api/itemPriceHooks";
import type { ItemPrice } from "@/lib/erp";
import { getListViewSchema } from "@/lib/views/listViewSchemas";
import type { ListViewFilter } from "@/lib/views/listViewSchemas/types";
import {
  getActiveListViewConfig,
  buildColumnsFromListViewConfig,
  convertListViewSortToTanStackSort,
  convertListViewFiltersToFilterDescriptors,
} from "@/lib/views/utils/buildListColumnsFromSchema";
import { useFilterStore } from "@/lib/store/filterStore";
import { ListQuickFiltersBar } from "@/components/list/ListQuickFiltersBar";
import { buildNewRoute } from "@/lib/config/docRoutes";
import { injectActionsColumn } from "@/features/list/actionsColumn";
import { GenericInlineDetailPanel } from "@/features/list/GenericInlineDetailPanel";
import { GenericDetailPanel } from "@/features/list/GenericDetailPanel";
import { useDetailPanelState } from "@/features/list/useDetailPanelState";
import { DollarSign } from "lucide-react";
import type { SubRowColumnInfo } from "@/features/common/components/ListTableBody";

export function ItemPriceListPage() {
  const navigate = useNavigate();
  const [inlineDetailDocName, setInlineDetailDocName] = useState<string | null>(null);
  const handleInlineClose = useCallback(() => setInlineDetailDocName(null), []);
  const { detailPanelItem, panelItem, openDetailPanel, closeDetailPanel } = useDetailPanelState();

  const renderSubRow = useCallback(
    (row: ItemPrice, columnInfo: SubRowColumnInfo): ReactNode | null => {
      if (!row.name || row.name !== inlineDetailDocName) return null;
      return (
        <GenericInlineDetailPanel
          key={row.name}
          doctype="Item Price"
          docName={row.name}
          rowData={row as unknown as Record<string, unknown>}
          onClose={handleInlineClose}
          columns={columnInfo}
        />
      );
    },
    [inlineDetailDocName, handleInlineClose],
  );

  // Get schema and view config
  const schema = getListViewSchema("Item Price");
  const viewConfig = getActiveListViewConfig(schema, "experience");

  // Initialize active quick filter from schema default
  const [activeQuickFilterId, setActiveQuickFilterId] = useState<string | null>(() => {
    const defaultFilter = viewConfig?.quickFilters?.find((f) => f.default);
    return defaultFilter?.id ?? null;
  });

  // Handle quick filter change
  const handleQuickFilterChange = (
    nextId: string | null,
    pillFilters: ListViewFilter[]
  ) => {
    setActiveQuickFilterId(nextId);

    // Compute effective filters: defaultFilters + pill filters
    const defaultFilters = viewConfig?.defaultFilters ?? [];
    const effectiveFilters = [
      ...defaultFilters,
      ...pillFilters,
    ];

    // Convert to FilterDescriptor[] and set in store
    const filterDescriptors = convertListViewFiltersToFilterDescriptors(
      effectiveFilters,
      "Item Price"
    );
    
    useFilterStore.getState().setFiltersForKey("inventory.item_price", filterDescriptors);
  };

  // Build columns from schema
  const buildColumns = useMemo(
    () => {
      if (!viewConfig) {
        // Fallback: return empty array (should not happen in dev)
        return () => [];
      }
      const schemaColumns = buildColumnsFromListViewConfig<ItemPrice>(viewConfig);
      return ({
        items: _items,
        visibleFieldOrder,
        availableFields
      }: {
        items: ItemPrice[];
        visibleFieldOrder?: string[];
        availableFields?: Array<{ fieldname: string; label: string; fieldtype?: string }>;
      }) => {
        void _items;
        void visibleFieldOrder;
        void availableFields;
        return injectActionsColumn(schemaColumns, {
          onToggleDetail: (name) => setInlineDetailDocName((prev) => prev === name ? null : name),
          onOpen: (name) => openDetailPanel(name),
        });
      };
    },
    [viewConfig]
  );

  // Apply initial quick filter on mount (if default exists)
  useEffect(() => {
    if (!viewConfig || !viewConfig.quickFilters || activeQuickFilterId === null) {
      return;
    }

    const activeFilter = viewConfig.quickFilters.find(
      (f) => f.id === activeQuickFilterId
    );

    if (activeFilter) {
      const defaultFilters = viewConfig.defaultFilters ?? [];
      const effectiveFilters = [
        ...defaultFilters,
        ...activeFilter.filters,
      ];

      const filterDescriptors = convertListViewFiltersToFilterDescriptors(
        effectiveFilters,
        "Item Price"
      );

      // Only set if store is empty (don't override URL filters)
      const currentFilters = useFilterStore.getState().scopedFilters["inventory.item_price"];
      if (!currentFilters || currentFilters.length === 0) {
        useFilterStore.getState().setFiltersForKey("inventory.item_price", filterDescriptors);
      }
    }
  }, [viewConfig, activeQuickFilterId]);

  // Get default sort from schema
  const defaultSorting = useMemo(() => {
    if (!viewConfig?.defaultSort) {
      return [{ id: "modified", desc: true }]; // Fallback
    }
    return convertListViewSortToTanStackSort(viewConfig.defaultSort);
  }, [viewConfig]);

  return (
    <div className={LIST_PAGE_SHELL}>
      <div>
        <DocTypeListPageShell<ItemPrice>
          doctype="Item Price"
          columns={buildColumns}
          filterStoreKey="inventory.item_price"
          useListHook={useItemPriceListWithPagination}
          defaultSorting={defaultSorting}
          pageSizeOptions={viewConfig?.pageSizeOptions ?? [20, 50, 100, 200, 400, 500]}
          searchPlaceholder="Search by name, item code, or price list..."
          title="Item Prices"
          newButtonLabel="New Item Price"
          onNewClick={() => navigate(buildNewRoute("Item Price"))}
          onRowClick={undefined}
          renderSubRow={renderSubRow}
          activeSubRowId={inlineDetailDocName ?? undefined}
          getRowId={(row) => row.name}
          inlineEditable
          isInlineEditMode
          compactMode
          noPadding
          fullWidth
          className="pt-0"
          tableContainerClassName="overflow-x-auto overflow-y-hidden border-y border-border bg-white dark:bg-neutral-950"
          breadcrumbs={[
            {
              label: "Item Prices",
              icon: (
                <span className="flex items-center justify-center h-5 w-5 rounded-xs bg-green-500 shrink-0">
                  <DollarSign className="h-3 w-3 text-white" />
                </span>
              ),
            },
          ]}
          enableViewModeSwitch={true}
          headerBelow={
            viewConfig?.quickFilters && viewConfig.quickFilters.length > 0 ? (
              <ListQuickFiltersBar
                doctype="Item Price"
                quickFilters={viewConfig.quickFilters}
                activeFilterId={activeQuickFilterId}
                onChange={handleQuickFilterChange}
              />
            ) : undefined
          }
          listSettingsConfig={{
            key: "inventory.item_price",
            meta: {
              doctype: "Item Price",
              includeFieldtypes: [
                "Data",
                "Currency",
                "Date",
                "Datetime",
                "Check",
                "Int",
                "Float",
                "Link",
                "Select",
                "Small Text",
                "Text",
                "Long Text",
              ],
              excludeFieldtypes: [
                "Section Break",
                "Column Break",
                "HTML",
                "Table",
                "Table MultiSelect",
                "Button",
                "Heading",
              ],
              excludeFieldnames: [
                "naming_series",
                "owner",
                "creation",
                "modified",
                "modified_by",
                "idx",
                "__islocal",
                "__unsaved",
                "amended_from",
                "amended_date",
                "_assign",
                "_comment_count",
                "_liked_by",
              ],
              respectInListView: true,
              requiredFieldnames: ["name", "item_code"],
              defaultVisibleFieldnames: viewConfig?.columns?.map((col) => col.fieldname) || [],
            },
          }}
        />
      </div>

      <GenericDetailPanel
        open={!!detailPanelItem}
        onClose={closeDetailPanel}
        doctype="Item Price"
        docName={panelItem?.name ?? ""}
        docLabel={panelItem?.label}
      />
    </div>
  );
}

